<script lang="ts">
  import FlamePanel from '../lib/algorithms/thinking-traces/FlamePanel.svelte';
  import { VARIANCE_TRACES } from '../lib/data/variance-traces';
  import taskSrc from '../lib/data/sonnet-variance/algo_evl.tsk?raw';
  import referenceSrc from '../lib/data/sonnet-variance/reference.lam?raw';
  import sonnet1 from '../lib/data/sonnet-variance/sonnet-1.lam?raw';
  import sonnet2 from '../lib/data/sonnet-variance/sonnet-2.lam?raw';
  import sonnet3 from '../lib/data/sonnet-variance/sonnet-3.lam?raw';
  import sonnet4 from '../lib/data/sonnet-variance/sonnet-4.lam?raw';
  import sonnet5 from '../lib/data/sonnet-variance/sonnet-5.lam?raw';

  const taskDescription = taskSrc.split('\n---\n')[0].trim();

  const TASK_URL =
    'https://github.com/VictorTaelin/lambench/blob/main/tsk/algo_evl.tsk';
  const REFERENCE_URL =
    'https://github.com/VictorTaelin/lambench/blob/main/lam/algo_evl.lam';

  const solutions: string[] = [sonnet1, sonnet2, sonnet3, sonnet4, sonnet5];

  // For each chart: array-positions of rows in temporal order (start asc, depth asc).
  // Highlighting traverses every span AND every leaf in the order they happen.
  const playSequences: number[][] = VARIANCE_TRACES.map((trace) =>
    trace.rows
      .map((r, idx) => ({ start: r.start, depth: r.depth, idx }))
      .sort((a, b) => a.start - b.start || a.depth - b.depth)
      .map((x) => x.idx),
  );

  // One play state per chart: which step in the playSequence (or null).
  const playState: (number | null)[] = $state(VARIANCE_TRACES.map(() => null));
  const timers: (ReturnType<typeof setInterval> | null)[] =
    VARIANCE_TRACES.map(() => null);

  const STEP_MS = 200;

  function play(i: number) {
    if (timers[i]) {
      clearInterval(timers[i]!);
      timers[i] = null;
      playState[i] = null;
      return;
    }
    const total = playSequences[i]!.length;
    playState[i] = 0;
    timers[i] = setInterval(() => {
      const cur = playState[i];
      if (cur === null || cur === undefined) return;
      const next = cur + 1;
      if (next >= total) {
        clearInterval(timers[i]!);
        timers[i] = null;
        setTimeout(() => { playState[i] = null; }, 800);
        return;
      }
      playState[i] = next;
    }, STEP_MS);
  }

  function currentArrayIdx(chartIdx: number): number | null {
    const ps = playState[chartIdx];
    if (ps === null || ps === undefined) return null;
    return playSequences[chartIdx]?.[ps] ?? null;
  }
</script>

<a
  class="byline"
  href="/"
>
  adamsohn.com
</a>

<section class="page">
  <header class="hero">
    <h1>&lambda;-bench variance: same prompt, five different runs</h1>
    <p class="summary">A single benchmark number is the average. Five runs on the same prompt show the variance — which is the part the average hides.</p>
  </header>

  <p class="lead">Repeating the same prompt against the same model is not redundant. It exposes what a single trial cannot:</p>
  <ul class="lead-list">
    <li>Whether the model takes the same path twice.</li>
    <li>Whether the errors it catches are the same errors.</li>
    <li>Whether success comes from a stable strategy or a lucky sample.</li>
  </ul>
  <p class="lead">These five charts are the same Sonnet 4.6 model, run on the same prompt — implement a beta-normalizer for Scott-encoded de Bruijn lambda terms in pure lambda calculus. Low effort, default settings. All five solutions pass the official 10-test LamBench suite.</p>
  <p class="lead"><a href="https://github.com/VictorTaelin/lambench" target="_blank" rel="noopener">LamBench</a> measures whether a model can solve these tasks — run the prompt, check the output against the test suite, record pass or fail. We ran the same prompt against Sonnet five times instead, and looked at the thinking trace each run produced. The model's output is probabilistic; the same input produces a different trace every time. A pass/fail number tells you whether the model solved the task. Five traces start to show <em>how</em> it solved the task — and how much that varies.</p>

  <figure class="error-chart">
    <figcaption>Error-correction segments per run, out of total thinking steps:</figcaption>
    {#each VARIANCE_TRACES as trace, i}
      {@const n = trace.stepCount}
      {@const errs = trace.rows.filter((r) => r.index < n && r.category === 'ERROR_CORRECTION').length}
      <div class="error-row">
        <span class="error-label">run {i + 1}</span>
        <div class="error-bar-bg">
          <div class="error-bar" style:width="{(errs / 10) * 100}%"></div>
        </div>
        <span class="error-num">{errs} / {n}</span>
      </div>
    {/each}
  </figure>

  <p class="lead">The runs are not the same internally. What varies is the shape of the work:</p>
  <ul class="lead-list">
    <li>Run length — from ~30 thinking steps to ~86, on identical input.</li>
    <li>Error rate — zero mid-stream corrections in one run, eight in another.</li>
    <li>Decomposition — different helpers, different orderings, different abstractions.</li>
  </ul>

  <header class="task-card">
    <p class="eyebrow">LamBench · algo_evl</p>
    <h2 class="task-title">Lambda calculus evaluator</h2>
    <pre class="task-text">{taskDescription}</pre>
    <p class="links">
      <a href={TASK_URL} target="_blank" rel="noopener">task →</a>
      <a href={REFERENCE_URL} target="_blank" rel="noopener">reference solution →</a>
    </p>
  </header>

  <details class="reference-details">
    <summary>Reference solution (human, 17 lines)</summary>
    <pre><code>{referenceSrc.trim()}</code></pre>
  </details>

  <details class="methods prompt-block">
    <summary>The exact classification prompt we send Opus</summary>
    <div class="methods-body">
      <p>Each segment gets its own Opus 4.6 call. The prompt below is sent verbatim, with two segments before and two after the target as context (the target is marked <code>→ TARGET →</code>). The model returns a single uppercase category name. Malformed output fails the build — no silent fallback.</p>
      <pre class="prompt-text"><code>{`You are classifying ONE segment of a language model's thinking trace into one cognitive category. The model is solving the LamBench algo_evl task: implementing a beta-normalizer for Scott-encoded de Bruijn lambda terms in pure lambda calculus.

**Categories:**
- TASK_SETUP — Reading the prompt, restating what needs to be done, recalling encodings/conventions/definitions verbatim. Examples: 'This is a lambda calculus meta-interpreter problem', 'Scott encoding: Lam(b) = λl.λa.λv. l b'.
- DECOMPOSITION — Choosing strategy, planning structure, picking a representation, deciding which helpers to write. Defining a helper or constructor counts as DECOMPOSITION because the model is choosing what to build. Examples: 'Defining the Y combinator', 'Implementing equality checking', '## Approach'.
- PROCEDURAL_TRACKING — Tracking algorithm state mid-execution: which case is being handled, what the current depth is, what variable holds what. Example: 'I'm now in the Lam case with depth = 2'.
- ARITHMETIC — RAW NUMERIC COMPUTATION ONLY: applying a rule to specific concrete values. **In a lambda calculus task there is rarely any ARITHMETIC.** Defining an arithmetic helper is NOT arithmetic — that is DECOMPOSITION.
- EXECUTION — Connective tissue, transitions, generic 'now I'll...'. Use sparingly; prefer a more specific category.
- VERIFICATION — Checking correctness: tracing a worked example, sanity-checking a rule, verifying termination.
- ERROR_CORRECTION — Detecting an error and correcting it. 'Wait', 'Actually', 'I see there's a bug', followed by an actual fix.
- SURRENDER — Giving up.
- SURRENDER_DELIBERATION — Hesitating about whether to continue. NOT 'should I include helper X' (that is DECOMPOSITION).

**Decision rules (apply in order):**
1. If the segment says 'wait/actually/I was wrong' AND proposes a fix → ERROR_CORRECTION.
2. If the segment performs a concrete numeric step → ARITHMETIC.
3. If the segment is checking, tracing, or sanity-validating → VERIFICATION.
4. If the segment defines/declares a helper, picks an algorithm, or describes 'what I'll do' → DECOMPOSITION.
5. If the segment names variables/depths/indices/cases mid-execution → PROCEDURAL_TRACKING.
6. If the segment quotes the prompt or recalls an encoding → TASK_SETUP.
7. If the segment is generic narration AND nothing more specific applies → EXECUTION.
8. Surrender categories only for actual quitting.

**Critical traps:**
- 'Defining natural numbers' / 'Implementing the Y combinator' is DECOMPOSITION, not ARITHMETIC.
- 'Considering whether I need natAdd' is DECOMPOSITION, not SURRENDER_DELIBERATION.
- A segment that LOOKS arithmetic but is generic ('Now I'm working through the comparison and arithmetic operations') is DECOMPOSITION because no actual numbers are computed.

**Context** (segment to classify is marked with → TARGET →):

             [N-2] {{previous segment}}
             [N-1] {{previous segment}}
→ TARGET → [N]   {{the segment to classify}}
             [N+1] {{next segment}}
             [N+2] {{next segment}}

**Output:** the single uppercase category name for the TARGET segment. Nothing else.`}</code></pre>
      <p class="meta-note">
        Source: <code>/tmp/lambda-probe/classify_strict.py</code>. Per-segment + neighbor-context approach: each label is independently produced, but with enough surrounding text for Opus to disambiguate borderline cases.
      </p>
    </div>
  </details>

  <details class="methods" open>
    <summary>How thinking is classified (method)</summary>
    <div class="methods-body">
      <p>
        Each Sonnet run produces a thinking trace — a stream of internal text
        the model emits before answering. We don't see this normally; here we
        capture it via the API and turn it into a flame chart.
      </p>
      <p><strong>Two passes, both with Opus 4.6 (low effort), all 5 traces in parallel:</strong></p>
      <ol>
        <li>
          <strong>Per-segment categorization.</strong> We split the trace at
          newlines into ~30–130 line-segments per run. We send all segments
          for a run to Opus with the 9 category definitions and ask it to
          assign exactly one category per segment. Opus returns a JSON array
          of <code>{'{'}index, category{'}'}</code>.
        </li>
        <li>
          <strong>Nested sub-task tree.</strong> A second Opus call on the
          same segments asks for a <em>flat list of <code>{'{'}start, end,
          label{'}'}</code> ranges</em> describing the implicit hierarchy of
          sub-tasks. Ranges nest by containment — a range
          <code>[42, 44]</code> inside <code>[38, 50]</code> inside
          <code>[0, 80]</code> has containment-depth 2.
        </li>
      </ol>
      <p>
        Depth = number of ranges that strictly contain a given range. Each
        segment's leaf row sits one depth below its innermost containing
        sub-task. Bars at the same depth are guaranteed non-overlapping and
        time-ordered (left-to-right = chronologically forward).
      </p>

      <p class="cat-heading">The 9 categories, with real examples from these runs:</p>
      <div class="cat-grid">
        <div class="cat-row">
          <span class="cat-pill" style:background="#6c757d">Setup</span>
          <span class="cat-desc">Reading the prompt, restating the problem, recalling encodings.</span>
          <code class="cat-eg">"Given a Scott-encoded lambda term (with de Bruijn indices), reduce it to beta-normal form."</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#fd7e14">Decomposition</span>
          <span class="cat-desc">Choosing strategy, planning structure, deciding which helpers to write.</span>
          <code class="cat-eg">"Now I'm setting up the core structure with Scott-encoded natural numbers..."</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#0d6efd">State</span>
          <span class="cat-desc">Tracking depth, index, or case mid-execution.</span>
          <code class="cat-eg">"The nf is the result of eval f. Then I apply nf to three handlers, pattern-matching on whether it's Lam, App, or Var."</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#198754">Arithmetic</span>
          <span class="cat-desc">Raw numeric computation only — applying a rule to specific values.</span>
          <code class="cat-eg cat-eg-empty">(near-zero in these runs — lambda calculus rarely has actual arithmetic)</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#adb5bd; color: #333">Execution</span>
          <span class="cat-desc">Connective tissue, transitions ("now I'll...", "let me write...").</span>
          <code class="cat-eg">"OK, the implementation looks complete. Let me write it out cleanly."</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#6f42c1">Verification</span>
          <span class="cat-desc">Worked examples, sanity checks, validating a step or rule.</span>
          <code class="cat-eg">"Now I'm checking the subst function to make sure it handles variable substitution and de-indexing correctly."</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#dc3545">Error Correction</span>
          <span class="cat-desc">"Wait that's wrong" — detecting an error and fixing it.</span>
          <code class="cat-eg">"Actually, let me reconsider one thing. In the eval App handler:"</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#dc3545">Surrender</span>
          <span class="cat-desc">Giving up on the task.</span>
          <code class="cat-eg cat-eg-empty">(none in these runs — all 5 succeeded)</code>
        </div>
        <div class="cat-row">
          <span class="cat-pill" style:background="#fd7e14">Deliberation</span>
          <span class="cat-desc">Hesitating about whether to keep trying.</span>
          <code class="cat-eg cat-eg-empty">(none in these runs)</code>
        </div>
      </div>

      <p class="meta-note">
        Every classification on this page is done by Opus 4.6 — both the
        per-segment categorization and the nested-range tree. No smaller
        model is involved at any step. The lambda task produces few
        "Surrender"-class segments because all 5 runs succeed, but
        Verification, Error Correction, and Decomposition fire often as
        the model traces β-reduction examples and revises its substitution
        rules.
      </p>
    </div>
  </details>

  <h2>Sonnet 4.6 (low effort) × 5 runs</h2>

  {#each VARIANCE_TRACES as trace, i}
    <article class="run">
      <div class="run-controls">
        <button class="play-btn" onclick={() => play(i)}>
          {timers[i] ? '■ stop' : '▶ play in temporal order'}
        </button>
        {#if playState[i] !== null && playState[i] !== undefined}
          <span class="play-status">{(playState[i] ?? 0) + 1} / {playSequences[i]?.length ?? 0}</span>
        {/if}
      </div>
      <FlamePanel
        {trace}
        titleOverride={`run ${i + 1}`}
        outcome={{ label: '10/10 passed', tone: 'ok' }}
        showLegend={i === 0}
        maxChartHeight={320}
        forceSelectedIndex={currentArrayIdx(i)}
      />
      <details class="solution">
        <summary>Solution program ({solutions[i].split('\n').length} lines)</summary>
        <pre><code>{solutions[i].trim()}</code></pre>
      </details>
    </article>
  {/each}
</section>

<style>
  .page {
    max-width: 980px;
    margin: 0 auto;
    padding: 48px 24px;
    font-family: ui-sans-serif, -apple-system, system-ui, sans-serif;
    color: #1a1a1a;
    line-height: 1.5;
  }
  .task-card {
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    background: #fafafa;
    padding: 20px 24px;
    margin-bottom: 24px;
  }
  .eyebrow {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #888;
    margin: 0 0 4px 0;
  }
  .hero {
    margin: 0 0 28px 0;
    padding: 0;
    border: 0;
    background: none;
  }
  .hero h1 {
    font-family: var(--font-serif, "Source Serif Pro", ui-serif, Georgia, serif);
    font-size: 32px;
    font-weight: 600;
    line-height: 1.2;
    margin: 0 0 12px 0;
    color: #1a1a1a;
  }
  .summary {
    font-family: var(--font-serif, "Source Serif Pro", ui-serif, Georgia, serif);
    font-style: italic;
    font-size: 17px;
    color: #555;
    line-height: 1.5;
    margin: 0;
  }
  .task-title {
    font-size: 22px;
    font-weight: 600;
    font-family: ui-sans-serif, -apple-system, system-ui, sans-serif;
    margin: 0 0 12px 0;
    color: #1a1a1a;
  }
  h2 {
    font-size: 16px;
    font-weight: 500;
    color: #555;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    margin: 32px 0 16px 0;
  }
  .byline {
    position: fixed;
    top: 18px;
    right: 18px;
    z-index: 40;
    font-family: ui-sans-serif, -apple-system, system-ui, sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: #888;
    text-decoration: none;
    padding: 4px 8px;
    border-radius: 4px;
    transition: color 150ms ease, background 150ms ease;
  }
  .byline:hover,
  .byline:focus-visible {
    color: #2257b3;
    background: #f0f0f0;
    outline: none;
  }
  @media (max-width: 900px) {
    .byline {
      top: 8px;
      right: 8px;
    }
  }
  .task-text {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.55;
    white-space: pre-wrap;
    color: #333;
    margin: 0 0 12px 0;
    background: transparent;
    border: 0;
    padding: 0;
  }
  .links {
    margin: 0;
    font-size: 13px;
    display: flex;
    gap: 16px;
  }
  .links a {
    color: #2257b3;
    text-decoration: none;
  }
  .links a:hover { text-decoration: underline; }
  .reference-details, .solution {
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    background: #fafafa;
    padding: 8px 14px;
    margin: 16px 0;
  }
  .reference-details summary, .solution summary {
    cursor: pointer;
    font-size: 13px;
    color: #555;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
  .reference-details pre, .solution pre {
    margin: 12px 0 4px 0;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    padding: 12px;
    overflow-x: auto;
  }
  .reference-details code, .solution code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.55;
    color: #1a1a1a;
    white-space: pre;
  }
  .run {
    margin-bottom: 36px;
    padding-top: 8px;
  }
  .run-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }
  .play-btn {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    padding: 4px 10px;
    border: 1px solid #c0c0c0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    color: #333;
  }
  .play-btn:hover { background: #f0f0f0; }
  .play-status {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: #666;
  }

  .methods {
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    background: #fafafa;
    padding: 10px 18px;
    margin: 16px 0 24px 0;
  }
  .methods summary {
    cursor: pointer;
    font-size: 13px;
    color: #333;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 500;
  }
  .methods-body {
    padding-top: 12px;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
  }
  .methods-body p { margin: 0 0 12px 0; }
  .methods-body ol { margin: 0 0 12px 0; padding-left: 22px; }
  .methods-body li { margin-bottom: 8px; }
  .methods-body code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    background: #efefef;
    padding: 1px 4px;
    border-radius: 2px;
  }
  .cat-heading {
    margin-top: 16px !important;
    font-weight: 500;
  }
  .cat-grid {
    display: grid;
    grid-template-columns: 130px 1fr;
    gap: 4px 14px;
    margin: 6px 0 12px 0;
  }
  .cat-row {
    display: contents;
  }
  .cat-pill {
    color: #fff;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 3px;
    text-align: center;
    align-self: start;
    white-space: nowrap;
  }
  .cat-desc {
    grid-column: 2;
    font-size: 13px;
    color: #444;
  }
  .cat-eg {
    grid-column: 2;
    font-size: 12px;
    background: #fff !important;
    border: 1px solid #e0e0e0;
    padding: 4px 8px !important;
    margin-top: 2px;
    margin-bottom: 8px;
    color: #555;
    display: block;
  }
  .cat-eg-empty {
    color: #aaa;
    font-style: italic;
    background: transparent !important;
    border: 1px dashed #ddd;
  }
  .meta-note {
    font-size: 12px;
    color: #666;
    border-top: 1px solid #e0e0e0;
    padding-top: 12px;
    margin-top: 8px !important;
  }
  .prompt-block {
    margin-top: 12px;
  }
  .prompt-text {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11.5px;
    line-height: 1.55;
    background: #fff !important;
    border: 1px solid #d0d0d0 !important;
    padding: 14px 16px !important;
    border-radius: 4px;
    overflow-x: auto;
    white-space: pre-wrap;
    color: #333;
    margin: 8px 0 !important;
  }
  .prompt-text code {
    background: transparent !important;
    padding: 0 !important;
    border: 0 !important;
    font-size: 11.5px;
    white-space: pre-wrap;
  }
  .lead {
    margin: 0 0 12px 0;
    color: #333;
    font-size: 15px;
    line-height: 1.6;
  }
  .lead-list {
    margin: 0 0 24px 0;
    padding-left: 22px;
    color: #333;
    font-size: 15px;
    line-height: 1.6;
  }
  .lead-list li { margin-bottom: 6px; }

  .error-chart {
    margin: 0 0 24px 0;
    padding: 14px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fafafa;
  }
  .error-chart figcaption {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: #666;
    margin-bottom: 10px;
  }
  .error-row {
    display: grid;
    grid-template-columns: 60px 1fr 70px;
    align-items: center;
    gap: 10px;
    margin-bottom: 4px;
  }
  .error-label {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: #555;
  }
  .error-bar-bg {
    height: 14px;
    background: #efefef;
    border-radius: 2px;
    overflow: hidden;
  }
  .error-bar {
    height: 100%;
    background: #dc3545;
    transition: width 200ms;
  }
  .error-num {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    color: #555;
    text-align: right;
  }
</style>
